const BEFORE_SCREENSHOT_MAX_LAG_MS = 80;
const AFTER_SCREENSHOT_MIN_LEAD_MS = -50;
const AFTER_SCREENSHOT_MAX_DRIFT_MS = 700;
const BEFORE_ANCHOR_OFFSET_MS = 120;
export function getExpectedScreenshotTimestamp(eventTimestamp, timing, stepEndMs) {
    if (timing === 'before') {
        return eventTimestamp - BEFORE_ANCHOR_OFFSET_MS;
    }
    const endMs = typeof stepEndMs === 'number' ? stepEndMs : eventTimestamp;
    return Math.max(eventTimestamp + 450, endMs + 200);
}
function isReliableScreenshotTiming(screenshot, eventTimestamp, timing, stepEndMs) {
    if (screenshot.source === 'video_auto_heal' || screenshot.confidence === 'video_derived') {
        return true;
    }
    if (!Number.isFinite(screenshot.timestamp) || !Number.isFinite(eventTimestamp)) {
        return false;
    }
    if (timing === 'before') {
        return screenshot.timestamp <= eventTimestamp + BEFORE_SCREENSHOT_MAX_LAG_MS;
    }
    if (screenshot.timestamp < eventTimestamp + AFTER_SCREENSHOT_MIN_LEAD_MS) {
        return false;
    }
    return (screenshot.timestamp <=
        getExpectedScreenshotTimestamp(eventTimestamp, timing, stepEndMs) + AFTER_SCREENSHOT_MAX_DRIFT_MS);
}
function pickBestEventIndexedScreenshot(screenshots, eventTimestamp, timing, stepEndMs) {
    const expectedTimestamp = getExpectedScreenshotTimestamp(eventTimestamp, timing, stepEndMs);
    let best;
    let bestScore = null;
    for (const screenshot of screenshots) {
        const reliabilityScore = isReliableScreenshotTiming(screenshot, eventTimestamp, timing, stepEndMs)
            ? 1
            : 0;
        const confidenceScore = screenshot.source === 'video_auto_heal' || screenshot.confidence === 'video_derived'
            ? 2
            : screenshot.confidence === 'event_index'
                ? 1
                : 0;
        const diffScore = Math.abs(screenshot.timestamp - expectedTimestamp);
        const score = [reliabilityScore, confidenceScore, -diffScore];
        if (!bestScore ||
            score[0] > bestScore[0] ||
            (score[0] === bestScore[0] && score[1] > bestScore[1]) ||
            (score[0] === bestScore[0] && score[1] === bestScore[1] && score[2] > bestScore[2])) {
            best = screenshot;
            bestScore = score;
        }
    }
    return best;
}
function pickClosestScreenshot(screenshots, expectedTimestamp) {
    let best;
    let bestDiff = Number.POSITIVE_INFINITY;
    for (const screenshot of screenshots) {
        if (!Number.isFinite(screenshot.timestamp))
            continue;
        const diff = Math.abs(screenshot.timestamp - expectedTimestamp);
        if (diff < bestDiff) {
            best = screenshot;
            bestDiff = diff;
        }
        else if (diff === bestDiff && best && screenshot.index < best.index) {
            best = screenshot;
        }
    }
    return best;
}
function representationFromScreenshot(screenshot, timing) {
    const timelineMs = Number.isFinite(screenshot.timestamp)
        ? Math.max(0, screenshot.timestamp)
        : 0;
    return {
        screenshot,
        screenshotIndex: screenshot.index,
        timelineMs,
        timing,
    };
}
/**
 * Resolves the screenshot and timeline position Cricket Studio should show for a viewer step.
 * Keeps filmstrip, video seek, step time, and Change screen aligned with SOP before-screenshot semantics.
 */
export function resolveViewerStepRepresentation(args) {
    const { event, eventIndex, screenshots, screenshotPicks, stepEndMs } = args;
    const anchorTimestamp = event.timestamp;
    const fallbackTimelineMs = Math.max(0, anchorTimestamp - BEFORE_ANCHOR_OFFSET_MS);
    const pinnedIndex = screenshotPicks?.[String(eventIndex)];
    if (typeof pinnedIndex === 'number' && Number.isFinite(pinnedIndex)) {
        const pinned = screenshots.find((shot) => shot.index === pinnedIndex);
        if (pinned) {
            return representationFromScreenshot(pinned, 'pinned');
        }
    }
    const forEvent = screenshots.filter((shot) => shot.event_index === eventIndex);
    const beforeTimed = pickBestEventIndexedScreenshot(forEvent.filter((shot) => shot.timing === 'before'), anchorTimestamp, 'before', stepEndMs);
    if (beforeTimed) {
        return representationFromScreenshot(beforeTimed, 'before');
    }
    const beforeUntimed = pickClosestScreenshot(forEvent.filter((shot) => !shot.timing), getExpectedScreenshotTimestamp(anchorTimestamp, 'before', stepEndMs));
    if (beforeUntimed) {
        return representationFromScreenshot(beforeUntimed, 'before');
    }
    const legacy = screenshots.find((shot) => typeof shot.event_index !== 'number' && shot.index === eventIndex);
    if (legacy) {
        return representationFromScreenshot(legacy, 'fallback');
    }
    const afterTimed = pickBestEventIndexedScreenshot(forEvent.filter((shot) => shot.timing === 'after'), anchorTimestamp, 'after', stepEndMs);
    if (afterTimed) {
        return representationFromScreenshot(afterTimed, 'after');
    }
    const afterUntimed = pickClosestScreenshot(forEvent.filter((shot) => shot.timing !== 'before'), getExpectedScreenshotTimestamp(anchorTimestamp, 'after', stepEndMs));
    if (afterUntimed) {
        return representationFromScreenshot(afterUntimed, 'after');
    }
    return {
        timelineMs: fallbackTimelineMs,
        timing: 'fallback',
    };
}
